import { css } from '@emotion/react';
import axios from 'axios';
import type { AxiosError } from 'axios';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Button,
  Card,
  color,
  CheckBox,
  FormGroup,
  Icon,
  Input,
  Modal,
  spacing,
  typography,
  Tooltip,
  Typography,
  toast
} from '@uniquegood/realworld-studio-design';
import download from 'downloadjs';
import * as XLSX from 'xlsx';

import {
  AlignCenterFlex,
  CardItemGap,
  CursorPointer,
  DisplayNone,
  MarginBottom24,
  MarginBottom8,
  MarginBottomZero,
  MarginLeft8,
  MarginRight4,
  MarginRight8,
  MarginTop24,
  MarginTop8,
  MarginZero
} from '@/styles';
import { onMobile } from '@/styles/responsive';
import { useModalState, usePromotionModal, useRequest } from '@/hooks';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { keyPressHandler } from '@/utils';
import { PromotionTriggerLocation } from '@/track';
import { informationByMemberLevelList } from '@/pages/settings/const';
import { InformationByMemberLevelType, TypeUserLevel } from '@/pages/settings/types';
import { levelToKorean } from '@/const';

import MemberList from './MemberList';
import { CORE_API_BASE_URL, coreApi } from '@/api';
import AddMemberModalContent from './AddMemberModalContent';
import { BaseMemberCardStyle } from './MemberCard';
import ChangeTesterContentModalContent from './ChangeTesterContentModalContent';

// ApplicationMemberModel을 User로 변환하는 헬퍼 함수
function convertToUser(member: ApplicationMemberModel, level: TypeUserLevel): User {
  return {
    id: member.id ?? '',
    name: member.name ?? '',
    profileImage: member.profileImageUrl ?? '',
    level,
    isCreator: true
  } as User;
}

// ApplicationMembersResponseModel을 User[]로 변환하는 헬퍼 함수
function convertMembersResponseToUsers(response: ApplicationMembersResponseModel): User[] {
  const users: User[] = [];

  response.owners?.forEach((member) => {
    users.push(convertToUser(member, 'Owner'));
  });

  response.authors?.forEach((member) => {
    users.push(convertToUser(member, 'Author'));
  });

  response.testers?.forEach((member) => {
    users.push(convertToUser(member, 'Tester'));
  });

  return users;
}

const OrganizeMembersCardHeaderItem = {
  prefix: <Icon icon="user_friends_solid" size="20px" />,
  content: '구성원 편집'
};

const SubAccountCardHeaderItem = {
  prefix: <Icon icon="user_solid" size="20px" />,
  content: '서브 계정 일괄 생성'
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*[^A-Za-z0-9]).{8,}$/;
const DEFAULT_SUB_ACCOUNT_EMAIL_DOMAIN = 'rwd.to';

const SubAccountDownloadInfo = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.medium};
`;

const SubAccountDownloadActions = css`
  display: flex;
  gap: ${spacing.common.small};
`;

type SubAccountField = 'name' | 'email' | 'password';

type SubAccountDraft = {
  key: string;
  name: string;
  email: string;
  password: string;
};

type SubAccountRowItemProps = {
  draft: SubAccountDraft;
  error: Partial<Record<SubAccountField, string>>;
  isCreating: boolean;
  canRemove: boolean;
  onChange: (key: string, field: SubAccountField, value: string) => void;
  onRemove: (key: string) => void;
};

const createEmptySubAccountDraft = (): SubAccountDraft => ({
  key: `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  name: '',
  email: '',
  password: ''
});

const createSubAccountDraft = (name: string, email: string, password: string): SubAccountDraft => ({
  ...createEmptySubAccountDraft(),
  name,
  email,
  password
});

const SUB_ACCOUNT_HEADER_ALIASES: Record<SubAccountField, string[]> = {
  name: ['name', '이름', '성명'],
  email: ['email', 'e-mail', '이메일', '아이디', '아이디(이메일)', 'id'],
  password: ['password', 'pw', 'pass', '비밀번호', '패스워드']
};

const SUB_ACCOUNT_CSV_HEADERS = ['name', 'email', 'password'] as const;

type ApiErrorPayload = {
  message?: string;
  errorTitle?: string;
};

type ApiSuccessResponse<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

type JoinResponsePayload = {
  id?: string;
  userId?: string;
  accountId?: string;
  user?: { id?: string };
};

const isAxiosError = <T = unknown>(error: unknown): error is AxiosError<T> =>
  typeof error === 'object' &&
  error !== null &&
  (error as AxiosError<T>).isAxiosError;

const isJoinResponsePayload = (value: unknown): value is JoinResponsePayload =>
  typeof value === 'object' && value !== null;

const copyTextToClipboard = async (text: string) => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.pointerEvents = 'none';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error('클립보드 복사에 실패했어요.');
  }
};

const coreApiNoAuth = axios.create({ baseURL: CORE_API_BASE_URL });

const toCsvValue = (value: string) => {
  const normalized = value ?? '';
  const escaped = normalized.replace(/"/g, '""');
  return /[",\n\r]/.test(escaped) ? `"${escaped}"` : escaped;
};

const buildSubAccountCsv = (drafts: SubAccountDraft[]) => {
  const rows = drafts.map((draft) =>
    [draft.name, draft.email, draft.password].map(toCsvValue).join(',')
  );

  return [SUB_ACCOUNT_CSV_HEADERS.join(','), ...rows].join('\r\n');
};

const downloadSubAccountCsv = (drafts: SubAccountDraft[]) => {
  if (drafts.length === 0) return;
  const csv = buildSubAccountCsv(drafts);
  const dateStamp = new Date().toISOString().slice(0, 10);
  download(csv, `sub-accounts-${dateStamp}.csv`, 'text/csv');
};

const normalizeHeader = (value: unknown) => String(value ?? '').trim().toLowerCase();

const appendDefaultEmailDomain = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes('@')) return trimmed;
  return `${trimmed}@${DEFAULT_SUB_ACCOUNT_EMAIL_DOMAIN}`;
};

const shouldShowEmailSuffix = (value: string) => !value.trim().includes('@');

const resolveLimit = (limit: number | null | undefined, fallback: number) => {
  if (limit === null || limit === undefined) {
    return fallback;
  }
  return limit;
};

async function updateMember(appId: string | undefined, memberId: string, level: TypeUserLevel) {
  return coreApi.put(
    `/apps/${appId}/members/${memberId}`,
    { level },
    { headers: { 'x-rwd-api-version': '1.2' } }
  );
}

async function deleteMember(appId: string | undefined, memberId: string) {
  return coreApi.delete(`/apps/${appId}/members/${memberId}`, {
    headers: { 'x-rwd-api-version': '1.2' }
  });
}

async function addMember(appId: string, userId: string, level: TypeUserLevel) {
  return coreApi.post(
    `/apps/${appId}/members`,
    { userId, level },
    { headers: { 'x-rwd-api-version': '1.2' } }
  );
}

async function addMemberSilent(appId: string, userId: string, level: TypeUserLevel) {
  return coreApi.post(
    `/apps/${appId}/members`,
    { userId, level },
    { headers: { 'x-rwd-api-version': '1.2' }, skipGlobalErrorToast: true }
  );
}

const getErrorStatus = (error: unknown) =>
  isAxiosError<ApiErrorPayload>(error) ? error.response?.status : undefined;

const getErrorTitle = (error: unknown) =>
  isAxiosError<ApiErrorPayload>(error) ? error.response?.data?.errorTitle : undefined;

const getErrorMessage = (error: unknown) =>
  isAxiosError<ApiErrorPayload>(error) ? error.response?.data?.message : undefined;

const isNotFoundUserError = (error: unknown) => {
  const status = getErrorStatus(error);
  const errorTitle = getErrorTitle(error);
  return status === 404 || errorTitle === 'NotFoundUser';
};

const loginSubAccount = async (email: string, password: string) => {
  const { data: loginData } = await coreApiNoAuth.post(
    '/api/auth/login',
    { id: email, password, provider: 'Self' },
    { headers: { 'x-rwd-api-version': '1.1' } }
  );

  const token = loginData?.data?.token ?? loginData?.token;
  if (!token) {
    throw new Error('계정 로그인 토큰을 가져오지 못했어요.');
  }

  return token as string;
};

const resolveUserIdByLogin = async (email: string, password: string) => {
  const token = await loginSubAccount(email, password);

  const { data: meData } = await coreApiNoAuth.get('/api/me', {
    headers: { 'x-rwd-api-version': '1.2', Authorization: `Bearer ${token}` }
  });

  const userId = meData?.data?.id ?? meData?.id;
  if (!userId) {
    throw new Error('생성된 계정 ID를 찾지 못했어요.');
  }

  return userId as string;
};

const setPhoneVerifiedForSubAccount = async (email: string, password: string) => {
  const token = await loginSubAccount(email, password);
  await coreApiNoAuth.put(
    '/api/me',
    { phoneVerified: true },
    { headers: { 'x-rwd-api-version': '1.2', Authorization: `Bearer ${token}` } }
  );
};

async function checkSubAccountEmailAvailability(email: string) {
  return coreApi.post(
    '/api/auth/id/check',
    { email },
    { headers: { 'x-rwd-api-version': '1.0' }, skipGlobalErrorToast: true }
  );
}

const SubAccountRowItem = memo(function SubAccountRowItem({
  draft,
  error,
  isCreating,
  canRemove,
  onChange,
  onRemove
}: SubAccountRowItemProps) {
  const emailInput = (
    <Input
      type="email"
      value={draft.email}
      onChange={(value) => onChange(draft.key, 'email', value)}
      cssStyle={SubAccountEmailInput}
      onBlur={() => {
        const normalizedEmail = appendDefaultEmailDomain(draft.email);
        if (normalizedEmail !== draft.email) {
          onChange(draft.key, 'email', normalizedEmail);
        }
      }}
      placeholder="example"
      suffix={
        <span css={SubAccountEmailSuffix(shouldShowEmailSuffix(draft.email))}>
          @{DEFAULT_SUB_ACCOUNT_EMAIL_DOMAIN}
        </span>
      }
      error={Boolean(error.email)}
    />
  );

  return (
    <div css={SubAccountRow}>
      <FormGroup label="아이디(이메일)" requiredIndicator errorText={error.email}>
        {emailInput}
      </FormGroup>
      <FormGroup label="이름" requiredIndicator errorText={error.name}>
        <Input
          value={draft.name}
          onChange={(value) => onChange(draft.key, 'name', value)}
          placeholder="예) 홍길동"
          error={Boolean(error.name)}
        />
      </FormGroup>
      <FormGroup label="비밀번호" requiredIndicator errorText={error.password}>
        <Input
          type="password"
          value={draft.password}
          onChange={(value) => onChange(draft.key, 'password', value)}
          placeholder="비밀번호"
          error={Boolean(error.password)}
        />
      </FormGroup>
      <div css={SubAccountRowActions}>
        <Button
          type="plainDestructive"
          icon="trash_alt_solid"
          onClick={() => onRemove(draft.key)}
          disabled={isCreating || !canRemove}
        />
      </div>
    </div>
  );
});

function Members() {
  const { modal, openModal, closeModal } = useModalState();
  const { appId } = useParams<AppParam>();
  const { plan } = usePlanLimits();
  const isClassPlan = plan === 'Class';
  const { openPromotionModal } = usePromotionModal();
  const subAccountFileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    data: { data: membersResponse } = { data: { owners: [], authors: [], testers: [] } },
    mutate: mutateMembers
  } = useRequest<CommonResponseModel<ApplicationMembersResponseModel>>(
    `/apps/${appId}/members`,
    {},
    { headers: { 'x-rwd-api-version': '1.2' } }
  );

  const {
    data: { data: memberLimit } = {
      data: { ownerLimit: null, authorLimit: null, testerLimit: null }
    }
  } = useRequest<CommonResponseModel<ApplicationMemberLimitInfoResponseModel>>(
    `/apps/${appId}/members/limit`
  );

  // API 응답을 User[] 형태로 변환 (객체 참조 대신 내용 기반으로 의존성 설정)
  const membersResponseKey = JSON.stringify(membersResponse);
  const members = useMemo(
    () => convertMembersResponseToUsers(membersResponse),
    [membersResponseKey]
  );

  const [subAccountDrafts, setSubAccountDrafts] = useState<SubAccountDraft[]>([
    createEmptySubAccountDraft()
  ]);
  const [subAccountErrors, setSubAccountErrors] = useState<
    Record<string, Partial<Record<SubAccountField, string>>>
  >({});
  const [isCreatingSubAccounts, setIsCreatingSubAccounts] = useState(false);
  const [subAccountConsentChecked, setSubAccountConsentChecked] = useState(false);
  const [lastCreatedSubAccounts, setLastCreatedSubAccounts] = useState<SubAccountDraft[]>([]);

  const authorLimit = resolveLimit(memberLimit?.authorLimit, 25);
  const ownerLimit = resolveLimit(memberLimit?.ownerLimit, 3);
  const testerLimit = resolveLimit(memberLimit?.testerLimit, 3);

  const updateSubAccountDraft = useCallback(
    (key: string, field: SubAccountField, value: string) => {
      setSubAccountDrafts((prev) =>
        prev.map((draft) => (draft.key === key ? { ...draft, [field]: value } : draft))
      );

      setSubAccountErrors((prev) => {
        if (!prev[key]?.[field]) return prev;

        const next = {
          ...prev,
          [key]: {
            ...prev[key],
            [field]: undefined
          }
        };

      if (next[key] && Object.values(next[key]).every((error) => !error)) {
          const rest = { ...next };
          delete rest[key];
          return rest;
        }

        return next;
      });
    },
    []
  );

  const addSubAccountRow = useCallback(() => {
    setSubAccountDrafts((prev) => [...prev, createEmptySubAccountDraft()]);
  }, []);

  const removeSubAccountRow = useCallback((key: string) => {
    setSubAccountDrafts((prev) =>
      prev.length > 1 ? prev.filter((draft) => draft.key !== key) : prev
    );
    setSubAccountErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const openFailImageModal = useCallback(() => {
    openModal({
      title: '최소 한 명 이상의 소유자가 필요해요',
      children: (
        <div>
          <Icon
            icon="times_circle_regular"
            size="100px"
            color={color.interactive_critical_default}
          />
          <Typography
            as="p"
            type="body"
            cssStyle={css`
              text-align: center;
            `}
          >
            다시 시도해주세요
          </Typography>
        </div>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      }
    });
  }, [openModal, closeModal]);

  const openEditorLimitModal = useCallback((limit: number) => {
    openModal({
      title: '편집자 수 제한에 도달했어요',
      children: (
        <div>
          <Icon
            icon="times_circle_regular"
            size="100px"
            color={color.interactive_critical_default}
          />
          <Typography
            as="p"
            type="body"
            cssStyle={css`
              text-align: center;
            `}
          >
            편집자는 최대 {limit}명까지 추가할 수 있어요
          </Typography>
        </div>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      }
    });
  }, [openModal, closeModal]);

  const openOwnerLimitModal = useCallback((limit: number) => {
    openModal({
      title: '소유자 수 제한에 도달했어요',
      children: (
        <div>
          <Icon
            icon="times_circle_regular"
            size="100px"
            color={color.interactive_critical_default}
          />
          <Typography
            as="p"
            type="body"
            cssStyle={css`
              text-align: center;
            `}
          >
            소유자는 최대 {limit}명까지 추가할 수 있어요
          </Typography>
        </div>
      ),
      primaryAction: {
        content: '확인',
        onAction: closeModal
      }
    });
  }, [openModal, closeModal]);

  const openSubAccountDownloadModal = useCallback(
    (createdDrafts: SubAccountDraft[]) => {
      if (createdDrafts.length === 0) return;
      const csv = buildSubAccountCsv(createdDrafts);

      openModal({
        title: '서브 계정 CSV 저장',
        children: (
          <div css={SubAccountDownloadInfo}>
            <Typography as="p" type="body">
              브라우저 설정에 따라 자동 다운로드가 차단될 수 있어요. 아래 버튼으로 CSV를
              다운로드하거나 복사해 안전하게 보관해주세요.
            </Typography>
            <Typography as="p" type="caption" textColor="subdued">
              이 정보는 현재 탭에서만 유지돼요. 페이지를 새로고침하거나 닫으면 다시 받을 수
              없어요.
            </Typography>
            <div css={SubAccountDownloadActions}>
              <Button
                type="secondaryBlue"
                onClick={async () => {
                  try {
                    await copyTextToClipboard(csv);
                    toast({ message: 'CSV가 클립보드에 복사됐어요.' });
                  } catch {
                    toast({
                      message: '복사에 실패했어요. 다운로드를 이용해주세요.',
                      type: 'error'
                    });
                  }
                }}
              >
                CSV 복사
              </Button>
            </div>
          </div>
        ),
        primaryAction: {
          content: '확인',
          onAction: closeModal
        },
        secondaryAction: {
          content: 'CSV 다운로드',
          onAction: () => downloadSubAccountCsv(createdDrafts)
        }
      });
    },
    [openModal, closeModal]
  );

  const handleOpenLastCreatedCsv = useCallback(() => {
    openSubAccountDownloadModal(lastCreatedSubAccounts);
  }, [openSubAccountDownloadModal, lastCreatedSubAccounts]);

  const handleCreateSubAccounts = useCallback(async () => {
    if (!appId) return;

    const normalizedDrafts = subAccountDrafts.map((draft) => ({
      ...draft,
      name: draft.name.trim(),
      email: appendDefaultEmailDomain(draft.email.trim())
    }));
    const shouldSyncDrafts = normalizedDrafts.some(
      (draft, index) => draft.email !== subAccountDrafts[index]?.email
    );

    if (shouldSyncDrafts) {
      setSubAccountDrafts(normalizedDrafts);
    }

    const draftsToCreate = normalizedDrafts.filter(
      (draft) => draft.name || draft.email || draft.password
    );

    if (draftsToCreate.length === 0) {
      toast({ message: '생성할 서브 계정을 입력해주세요.', type: 'error' });
      return;
    }

    if (!subAccountConsentChecked) {
      toast({ message: '개인정보 및 이용 약관 동의에 체크해주세요.', type: 'error' });
      return;
    }

    if (!isClassPlan) {
      openPromotionModal({ triggerLocation: PromotionTriggerLocation.invite_editor });
      return;
    }

    let latestMembers = members;
    let latestAuthorLimit = authorLimit;

    try {
      const [membersResponseLatest, memberLimitLatest] = await Promise.all([
        coreApi.get<CommonResponseModel<ApplicationMembersResponseModel>>(
          `/apps/${appId}/members`,
          { headers: { 'x-rwd-api-version': '1.2' }, skipGlobalErrorToast: true }
        ),
        coreApi.get<CommonResponseModel<ApplicationMemberLimitInfoResponseModel>>(
          `/apps/${appId}/members/limit`,
          { skipGlobalErrorToast: true }
        )
      ]);

      if (membersResponseLatest?.data?.data) {
        latestMembers = convertMembersResponseToUsers(membersResponseLatest.data.data);
      }

      if (memberLimitLatest?.data?.data) {
        latestAuthorLimit = resolveLimit(
          memberLimitLatest.data.data.authorLimit,
          authorLimit
        );
      }
    } catch {
      toast({
        message: '채널 멤버 제한 정보를 확인하지 못했어요. 잠시 후 다시 시도해주세요.',
        type: 'error'
      });
      return;
    }

    const editorCount = latestMembers.filter(
      (member) => member.level === 'Author' || member.level === 'Developer'
    ).length;

    if (editorCount + draftsToCreate.length > latestAuthorLimit) {
      openEditorLimitModal(latestAuthorLimit);
      return;
    }

    const nextErrors: Record<string, Partial<Record<SubAccountField, string>>> = {};
    const emailCounts = new Map<string, number>();

    draftsToCreate.forEach((draft) => {
      if (!draft.name) {
        nextErrors[draft.key] = {
          ...nextErrors[draft.key],
          name: '이름을 입력해주세요.'
        };
      }

      if (!draft.email) {
        nextErrors[draft.key] = {
          ...nextErrors[draft.key],
          email: '아이디(이메일)을 입력해주세요.'
        };
      } else if (!EMAIL_REGEX.test(draft.email)) {
        nextErrors[draft.key] = {
          ...nextErrors[draft.key],
          email: '이메일 형식을 확인해주세요.'
        };
      } else {
        const normalizedEmail = draft.email.toLowerCase();
        emailCounts.set(normalizedEmail, (emailCounts.get(normalizedEmail) ?? 0) + 1);
      }

      if (!draft.password) {
        nextErrors[draft.key] = {
          ...nextErrors[draft.key],
          password: '비밀번호를 입력해주세요.'
        };
      } else if (!PASSWORD_REGEX.test(draft.password)) {
        nextErrors[draft.key] = {
          ...nextErrors[draft.key],
          password: '8자 이상, 대문자 1자 이상, 특수문자 1자 이상을 포함해주세요.'
        };
      }
    });

    emailCounts.forEach((count, email) => {
      if (count <= 1) return;
      draftsToCreate.forEach((draft) => {
        if (draft.email.toLowerCase() !== email) return;
        nextErrors[draft.key] = {
          ...nextErrors[draft.key],
          email: '중복된 이메일이 있어요.'
        };
      });
    });

    if (Object.keys(nextErrors).length > 0) {
      setSubAccountErrors(nextErrors);
      toast({ message: '입력값을 확인해주세요.', type: 'error' });
      return;
    }

    setIsCreatingSubAccounts(true);
    try {
      const results = await Promise.all(
        draftsToCreate.map(async (draft) => {
          try {
            try {
              await checkSubAccountEmailAvailability(draft.email);
            } catch (checkError: unknown) {
              const status = getErrorStatus(checkError);
              let message =
                getErrorMessage(checkError) ||
                (checkError instanceof Error ? checkError.message : undefined);
              if (status === 409) {
                message = '이미 사용 중인 이메일이에요.';
              } else if (status === 410) {
                message = '탈퇴 진행 중인 이메일이에요. 다른 이메일을 사용해주세요.';
              } else if (!message) {
                message = '중복 체크에 실패했어요. 다시 시도해주세요.';
              }

              return { draft, ok: false as const, error: new Error(message) };
            }

            const { data } = await coreApi.post<ApiSuccessResponse<JoinResponsePayload>>(
              '/api/auth/join',
              {
                id: draft.email,
                name: draft.name,
                password: draft.password,
                provider: 'Self',
                userDataConsent: true,
                useConsent: true,
                ageConsent: true,
                receiveMarketingNotificationConsent: false
              },
              {
                headers: { 'x-rwd-api-version': '1.2' },
                skipGlobalErrorToast: true
              }
            );

            const payload = data?.data;
            const createdUserId =
              (typeof payload === 'string' && payload) ||
              (isJoinResponsePayload(payload)
                ? payload.id || payload.userId || payload.accountId || payload.user?.id
                : undefined);

            let addMemberError: unknown | null = null;
            if (createdUserId) {
              try {
                await addMemberSilent(appId, createdUserId, 'Author');
              } catch (error: unknown) {
                addMemberError = error;
              }
            }

            if (!createdUserId || (addMemberError && isNotFoundUserError(addMemberError))) {
              const fallbackUserId = await resolveUserIdByLogin(draft.email, draft.password);
              await addMemberSilent(appId, fallbackUserId, 'Author');
            } else if (addMemberError) {
              return { draft, ok: false as const, error: addMemberError };
            }

            await setPhoneVerifiedForSubAccount(draft.email, draft.password);
            return { draft, ok: true as const };
          } catch (error: unknown) {
            return { draft, ok: false as const, error };
          }
        })
      );

      const failedDrafts = results.filter((result) => !result.ok).map((result) => result.draft);
      const createdDrafts = results.filter((result) => result.ok).map((result) => result.draft);
      const successCount = results.length - failedDrafts.length;
      const apiErrors: Record<string, Partial<Record<SubAccountField, string>>> = {};

      results.forEach((result) => {
        if (result.ok) return;
        const message =
          getErrorMessage(result.error) ||
          (result.error instanceof Error ? result.error.message : undefined) ||
          '계정 생성에 실패했어요. 정보를 확인해주세요.';
        apiErrors[result.draft.key] = { email: message };
      });

      if (successCount > 0) {
        await mutateMembers();
        toast({ message: `${successCount}명의 서브 계정을 생성하고 편집자로 추가했어요!` });
        setLastCreatedSubAccounts(createdDrafts);
        openSubAccountDownloadModal(createdDrafts);
      }

      if (failedDrafts.length > 0) {
        toast({
          message: `${failedDrafts.length}건의 생성에 실패했어요. 오류를 확인해주세요.`,
          type: 'error'
        });
      }

      setSubAccountErrors(apiErrors);
      setSubAccountDrafts(
        failedDrafts.length > 0 ? failedDrafts : [createEmptySubAccountDraft()]
      );
    } finally {
      setIsCreatingSubAccounts(false);
    }
  }, [
    appId,
    subAccountDrafts,
    subAccountConsentChecked,
    isClassPlan,
    members,
    authorLimit,
    openPromotionModal,
    openEditorLimitModal,
    openSubAccountDownloadModal,
    mutateMembers
  ]);

  const handleSubAccountFileUpload = useCallback(() => {
    subAccountFileInputRef.current?.click();
  }, []);

  const handleSubAccountFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const file = input.files?.[0];
      input.value = '';
      if (!file) return;

      try {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];

        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
          blankrows: false
        }) as (string | number)[][];

        if (!rows.length) {
          toast({ message: '파일에 데이터가 없어요.', type: 'error' });
          return;
        }

        const headerRow = rows[0].map(normalizeHeader);
        const getHeaderIndex = (field: SubAccountField) =>
          headerRow.findIndex((header) => SUB_ACCOUNT_HEADER_ALIASES[field].includes(header));

        let nameIndex = getHeaderIndex('name');
        let emailIndex = getHeaderIndex('email');
        let passwordIndex = getHeaderIndex('password');

        const hasHeader =
          nameIndex !== -1 || emailIndex !== -1 || passwordIndex !== -1;

        let dataStartIndex = 0;

        if (hasHeader) {
          if (nameIndex === -1 || emailIndex === -1 || passwordIndex === -1) {
            toast({
              message: '헤더에 이름, 아이디(이메일), 비밀번호 컬럼이 모두 있어야 해요.',
              type: 'error'
            });
            return;
          }
          dataStartIndex = 1;
        } else {
          nameIndex = 0;
          emailIndex = 1;
          passwordIndex = 2;
        }

        const draftRows = rows.slice(dataStartIndex).filter((row) =>
          row.some((cell) => String(cell ?? '').trim())
        );

        const nextDrafts = draftRows.map((row) =>
          createSubAccountDraft(
            String(row[nameIndex] ?? '').trim(),
            appendDefaultEmailDomain(String(row[emailIndex] ?? '').trim()),
            String(row[passwordIndex] ?? '').trim()
          )
        );

        if (!nextDrafts.length) {
          toast({ message: '가져올 데이터가 없어요.', type: 'error' });
          return;
        }

        setSubAccountErrors({});
        setSubAccountDrafts((prev) => {
          const hasExistingValue = prev.some(
            (draft) => draft.name || draft.email || draft.password
          );
          return hasExistingValue ? [...prev, ...nextDrafts] : nextDrafts;
        });

        toast({ message: `${nextDrafts.length}건을 불러왔어요.` });
      } catch {
        toast({ message: '파일을 읽는 데 실패했어요. 형식을 확인해주세요.', type: 'error' });
      }
    },
    []
  );

  const isLastOwner = useCallback(
    (member: User) => {
      return member.level === 'Owner' && members.filter((m) => m.level === 'Owner').length === 1;
    },
    [members]
  );

  // 멤버 역할 업데이트 (즉시 API 호출)
  const handleUpdateMember = useCallback(
    async (member: User, level: TypeUserLevel) => {
      if (isLastOwner(member)) {
        openFailImageModal();
        return;
      }

      // Class / Enterprise 요금제일 때 소유자를 편집자로 변경하려고 할 때 편집자 수 제한 체크
      if (
        (plan === 'Class' || plan === 'Enterprise') &&
        member.level === 'Owner' &&
        level === 'Author'
      ) {
        const ownerCount = members.filter((m) => m.level === 'Owner').length;
        const editorCount = members.filter(
          (m) => m.level === 'Author' || m.level === 'Developer'
        ).length;

        // 소유자가 2명 이상이고 편집자가 25명 이상이면 변경 불가
        if (ownerCount >= 2 && editorCount >= authorLimit) {
          openEditorLimitModal(authorLimit);
          return;
        }
      }

      // Class / Enterprise 요금제일 때 테스터 또는 소유자를 편집자로 변경하려고 할 때 편집자 수 제한 체크
      if (
        (plan === 'Class' || plan === 'Enterprise') &&
        (member.level === 'Tester' || member.level === 'Owner') &&
        level === 'Author'
      ) {
        const editorCount = members.filter(
          (m) => m.level === 'Author' || m.level === 'Developer'
        ).length;

        // 편집자가 25명 이상이면 변경 불가
        if (editorCount >= authorLimit) {
          openEditorLimitModal(authorLimit);
          return;
        }
      }

      // Class / Enterprise 요금제일 때 편집자 또는 테스터를 소유자로 변경하려고 할 때 소유자 수 제한 체크
      if (
        (plan === 'Class' || plan === 'Enterprise') &&
        (member.level === 'Author' || member.level === 'Developer' || member.level === 'Tester') &&
        level === 'Owner'
      ) {
        const ownerCount = members.filter((m) => m.level === 'Owner').length;

        // 소유자가 3명 이상이면 변경 불가
        if (ownerCount >= ownerLimit) {
          openOwnerLimitModal(ownerLimit);
          return;
        }
      }

      // 소유자/편집자를 테스터로 변경하는 경우: 콘텐츠 할당 모달 표시
      if (
        (member.level === 'Owner' || member.level === 'Author' || member.level === 'Developer') &&
        level === 'Tester'
      ) {
        openModal({
          title: '테스트 콘텐츠 변경',
          children: (
            <ChangeTesterContentModalContent
              memberToConvert={{ id: member.id, name: member.name }}
              onSaveSuccess={() => {
                mutateMembers();
                closeModal();
              }}
            />
          ),
          size: 'big',
          hasFooter: false,
          isBottomRadius: true
        });
        return;
      }

      try {
        await updateMember(appId, member.id, level);
        await mutateMembers();
        toast({ message: `${member.name}님을 ${levelToKorean[level]}로 변경했어요!` });
      } catch {
        // 전역 에러 토스트가 자동으로 표시됨
      }
    },
    [
      isLastOwner,
      plan,
      members,
      authorLimit,
      ownerLimit,
      appId,
      mutateMembers,
      openFailImageModal,
      openEditorLimitModal,
      openOwnerLimitModal,
      openModal,
      closeModal
    ]
  );

  // 멤버 삭제 (즉시 API 호출)
  const handleRemoveMember = useCallback(
    async (member: User) => {
      if (isLastOwner(member)) {
        openFailImageModal();
        return;
      }

      openModal({
        isSmallScreen: true,
        title: '구성원을 삭제하시겠어요?',
        primaryAction: {
          content: '삭제',
          icon: 'trash_alt_solid',
          type: 'destructive',
          onAction: async () => {
            try {
              await deleteMember(appId, member.id);
              await mutateMembers();
              toast({
                message: `${member.name}님을 ${levelToKorean[member.level]}에서 삭제했어요!`
              });
              closeModal();
            } catch {
              // 전역 에러 토스트가 자동으로 표시됨
            }
          }
        },
        secondaryAction: {
          content: '취소',
          onAction: closeModal
        },
        children: '삭제한 구성원은 더 이상 이 채널에 접근할 수 없어요'
      });
    },
    [isLastOwner, appId, mutateMembers, openFailImageModal, openModal, closeModal]
  );

  // 멤버 추가 (즉시 API 호출)
  const handleAddMember = useCallback(
    async (selectedUser: AccountResponseModel, level: TypeUserLevel) => {
      if (!appId) return;

      // 이미 존재하는 멤버인지 확인
      const isAlreadyMember = members.some((member) => member.id === selectedUser.id);
      if (isAlreadyMember) {
        toast({ message: '이미 추가된 구성원이에요.', type: 'error' });
        return;
      }

      try {
        await addMember(appId, selectedUser.id, level);
        await mutateMembers();
        toast({ message: `${selectedUser.name}님을 ${levelToKorean[level]}로 추가했어요!` });
        closeModal();
      } catch {
        // 전역 에러 토스트가 자동으로 표시됨
      }
    },
    [appId, members, mutateMembers, closeModal]
  );

  // 테스터 추가 - 콘텐츠 할당 모달 열기 (API 호출은 모달 내에서 저장 시 수행)
  const handleAddTester = useCallback(
    (selectedUser: AccountResponseModel, level: TypeUserLevel) => {
      if (!level) return;
      if (!appId) return;

      // 이미 존재하는 멤버인지 확인
      const isAlreadyMember = members.some((member) => member.id === selectedUser.id);
      if (isAlreadyMember) {
        toast({ message: '이미 추가된 구성원이에요.', type: 'error' });
        return;
      }

      // 테스터 추가 모달 닫고 콘텐츠 할당 모달 열기
      // 실제 테스터 등록 API는 ChangeTesterContentModalContent에서 저장 시 호출됨
      closeModal();
      openModal({
        title: '테스트 콘텐츠 변경',
        children: (
          <ChangeTesterContentModalContent
            pendingTester={selectedUser}
            onSaveSuccess={() => {
              mutateMembers();
              closeModal();
            }}
          />
        ),
        size: 'big',
        hasFooter: false,
        isBottomRadius: true
      });
    },
    [appId, members, mutateMembers, closeModal, openModal]
  );

  // 멤버 레벨에 따른 triggerLocation 매핑
  const getTriggerLocationByLevel = (levelEn: string) => {
    switch (levelEn) {
      case 'Owner':
        return PromotionTriggerLocation.invite_owner;
      case 'Author':
        return PromotionTriggerLocation.invite_editor;
      case 'Tester':
        return PromotionTriggerLocation.invite_tester;
      default:
        return PromotionTriggerLocation.invite_editor;
    }
  };

  function onClickAddMemberBtn(level: InformationByMemberLevelType) {
    if (
      plan === 'Free' &&
      (level.en === 'Owner' || level.en === 'Author' || level.en === 'Tester')
    ) {
      openPromotionModal({ triggerLocation: getTriggerLocationByLevel(level.en) });
      return;
    }
    // 프로/티처일 때 편집자 또는 소유자 추가 시 PromotionModal 표시
    if ((plan === 'Pro' || plan === 'Teacher') && (level.en === 'Owner' || level.en === 'Author')) {
      openPromotionModal({ triggerLocation: getTriggerLocationByLevel(level.en) });
      return;
    }

    // Pro 요금제일 때 Tester가 3명 이상이면 PromotionModal 표시
    if ((plan === 'Pro' || plan === 'Teacher') && level.en === 'Tester') {
      const testerCount = members.filter((member) => member.level === 'Tester').length;
      if (testerCount >= testerLimit) {
        openPromotionModal({ triggerLocation: PromotionTriggerLocation.invite_tester });
        return;
      }
    }

    // Class/Enterprise 요금제일 때 Owner가 3명 이상이면 소유자 수 제한 모달 표시
    if ((plan === 'Class' || plan === 'Enterprise') && level.en === 'Owner') {
      const ownerCount = members.filter((member) => member.level === 'Owner').length;
      if (ownerCount >= ownerLimit) {
        openOwnerLimitModal(ownerLimit);
        return;
      }
    }

    // Class/Enterprise 요금제일 때 편집자가 25명 이상이면 편집자 수 제한 모달 표시
    if ((plan === 'Class' || plan === 'Enterprise') && level.en === 'Author') {
      const authorCount = members.filter((member) => member.level === 'Author').length;
      if (authorCount >= authorLimit) {
        openEditorLimitModal(authorLimit);
        return;
      }
    }

    // 테스터 추가 시에는 테스터 등록 후 콘텐츠 할당 모달을 열도록 처리
    if (level.en === 'Tester') {
      openModal({
        title: `${level.kr} 추가`,
        primaryAction: {
          content: '구성원 추가',
          type: 'primary',
          htmlType: 'submit',
          form: 'modal'
        },
        secondaryAction: {
          content: '취소',
          onAction: closeModal
        },
        children: <AddMemberModalContent level={level} onAddMemberToCurrentApp={handleAddTester} />
      });
      return;
    }

    // 소유자/편집자 추가 (클래스/엔터 요금제)
    openModal({
      title: `${level.kr} 추가`,
      primaryAction: {
        content: '구성원 추가',
        type: 'primary',
        htmlType: 'submit',
        form: 'modal',
        onAction: closeModal
      },
      secondaryAction: {
        content: '취소',
        onAction: closeModal
      },
      children: <AddMemberModalContent level={level} onAddMemberToCurrentApp={handleAddMember} />
    });
  }

  return (
    <>
      <Card header={OrganizeMembersCardHeaderItem}>
        {informationByMemberLevelList.map((level) => {
          const tooltipContentMarkup = (
            <div>
              {level.tooltip.map((t) => {
                return <p key={t.id}>{t.content}</p>;
              })}
            </div>
          );

          const addMemberHandler = () => onClickAddMemberBtn(level);

          const handleOpenTestContentModal = (userId: string) => {
            openModal({
              title: '테스트 콘텐츠 변경',
              children: (
                <ChangeTesterContentModalContent userId={userId} onSaveSuccess={closeModal} />
              ),
              size: 'big',
              hasFooter: false,
              isBottomRadius: true
            });
          };

          return (
            <div key={level.en} css={WrapMemberLevelCardStyle}>
              <div css={[AlignCenterFlex, MarginBottom8]}>
                <Typography as="p" type="body" textColor="initial" cssStyle={MarginRight4}>
                  {level.kr}
                </Typography>
                <Tooltip dismissOnMouseOut title={level.kr} content={tooltipContentMarkup}>
                  <Icon
                    icon="question_circle_solid"
                    size="16px"
                    color={color.icon_disabled}
                    cssStyle={CursorPointer}
                  />
                </Tooltip>
              </div>

              <div css={GridLayout}>
                {members && members.length > 0 && (
                  <MemberList
                    members={members}
                    level={level}
                    onUpdateMember={handleUpdateMember}
                    onRemoveMember={handleRemoveMember}
                    onOpenTestContentModal={handleOpenTestContentModal}
                  />
                )}
                <div
                  css={[AlignCenterFlex, AddMemberCardStyle, CursorPointer]}
                  tabIndex={0}
                  role="button"
                  onClick={addMemberHandler}
                  onKeyDown={keyPressHandler(addMemberHandler)}
                >
                  <Icon icon="plus_solid" size="16px" cssStyle={[MarginZero, MarginRight8]} />
                  <Typography as="p" type="body" textColor="initial" cssStyle={MarginLeft8}>
                    {level.kr} 추가
                  </Typography>
                </div>
              </div>

              <Typography as="p" type="body" textColor="subdued" cssStyle={MarginTop8}>
                {level.description}
              </Typography>
            </div>
          );
        })}
      </Card>
      {isClassPlan && (
        <div css={MarginTop24}>
          <Card header={SubAccountCardHeaderItem} cssStyle={CardItemGap}>
            <Typography as="p" type="body" textColor="subdued">
              여러 명의 서브 계정을 한 번에 생성할 수 있어요.
            </Typography>
            <Typography as="p" type="caption" textColor="subdued">
              비밀번호 규칙: 8자 이상, 대문자 1자 이상, 특수문자 1자 이상
            </Typography>
            <Typography as="p" type="caption" textColor="subdued">
              엑셀/CSV 첫 행은 이름, 아이디(이메일), 비밀번호 또는 name, email, password를
              사용해주세요.
            </Typography>
            <CheckBox
              checked={subAccountConsentChecked}
              onChange={(event) => setSubAccountConsentChecked(event.target.checked)}
              cssStyle={SubAccountConsent}
            >
              본인은 계정 일괄 생성에 필요한 개인정보처리방침, 이용약관, 연령 동의 등에 대해
              당사자들의 동의를 확보했으며, 이에 대한 책임은 본인에게 있음을 확인합니다.
            </CheckBox>
            <div css={SubAccountRows}>
              {subAccountDrafts.map((draft) => {
                const error = subAccountErrors[draft.key] ?? {};

                return (
                  <SubAccountRowItem
                    key={draft.key}
                    draft={draft}
                    error={error}
                    isCreating={isCreatingSubAccounts}
                    canRemove={subAccountDrafts.length > 1}
                    onChange={updateSubAccountDraft}
                    onRemove={removeSubAccountRow}
                  />
                );
              })}
            </div>
            <div css={SubAccountActions}>
              {lastCreatedSubAccounts.length > 0 && (
                <Button type="secondaryBlue" onClick={handleOpenLastCreatedCsv}>
                  최근 생성 CSV
                </Button>
              )}
              <input
                ref={subAccountFileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                css={DisplayNone}
                onChange={handleSubAccountFileChange}
              />
              <Button
                type="outlineBasic"
                onClick={handleSubAccountFileUpload}
                disabled={isCreatingSubAccounts}
              >
                엑셀/CSV 업로드
              </Button>
              <Button
                type="outlinePrimary"
                icon="plus_solid"
                onClick={addSubAccountRow}
                disabled={isCreatingSubAccounts}
              >
                행 추가
              </Button>
              <Button
                type="primary"
                onClick={handleCreateSubAccounts}
                loading={isCreatingSubAccounts}
              >
                일괄 생성
              </Button>
            </div>
          </Card>
        </div>
      )}
      <Modal {...modal} />
    </>
  );
}

export default memo(Members);

const WrapMemberLevelCardStyle = css`
  ${MarginBottom24};
  &:last-child {
    ${MarginBottomZero};
  }
`;

const GridLayout = css`
  display: grid;
  gap: ${spacing.margin.small};
  grid-template-columns: repeat(auto-fill, 182px);
`;

const AddMemberCardStyle = css`
  ${BaseMemberCardStyle};
  width: fit-content;
  padding: ${spacing.borderedPadding.xlarge} ${spacing.common.medium};
  transition: 0.2s;

  ${onMobile} {
    width: 100%;
    justify-content: initial;
  }

  &:hover {
    background-color: ${color.surface_default_hovered};
  }
`;

const SubAccountRows = css`
  display: flex;
  flex-direction: column;
  gap: ${spacing.common.large};
  margin-top: ${spacing.common.large};
`;

const SubAccountConsent = css`
  margin-top: ${spacing.common.medium};
`;

const SubAccountRow = css`
  display: grid;
  gap: ${spacing.common.medium};
  grid-template-columns: minmax(0, 2fr) minmax(0, 1.1fr) minmax(0, 1.1fr) auto;
  align-items: start;

  ${onMobile} {
    grid-template-columns: 1fr;
  }
`;

const SubAccountRowActions = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 36px;
  margin-top: calc(${typography.line.s2} + ${spacing.margin.xsmall});

  ${onMobile} {
    justify-content: flex-start;
    margin-top: 0;
  }
`;

const SubAccountActions = css`
  display: flex;
  gap: ${spacing.common.small};
  justify-content: flex-end;

  ${onMobile} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SubAccountEmailSuffix = (visible: boolean) => css`
  color: ${color.text_subdued};
  opacity: ${visible ? 1 : 0};
  transform: translateX(${visible ? '0' : '-4px'});
  transition: opacity 0.2s ease, transform 0.2s ease;
  pointer-events: none;
`;

const SubAccountEmailInput = css`
  input {
    text-overflow: clip;
  }

  input:not(:focus) {
    -webkit-mask-image: linear-gradient(
      90deg,
      #000 0%,
      #000 calc(100% - 24px),
      transparent
    );
    mask-image: linear-gradient(90deg, #000 0%, #000 calc(100% - 24px), transparent);
  }
`;
